/*
<purpose>
  Shared maximize stage for the TicketDetailComponent hosts (AntwortenSeite + ArchivSeite).
  Inline, it is a transparent flex wrapper; maximized, the SAME subtree flips to a fixed
  overlay above a dimmed backdrop. One interaction model, written once.
</purpose>
<dependencies>theme; shared header icons; portal to document.body (backdrop only).</dependencies>
<constraints>
  - NEVER portal or remount the children: unconfirmed KlaerfallEditor slot input and the
    contentEditable edit buffer live in component state and die on remount. The flip is
    style-only by design — keep it that way.
  - Layout Stability: a measured placeholder holds the children's in-flow height while
    the subtree is fixed, so surrounding layout never moves.
  - z-index 900 / backdrop 899: ReanalysisModal + toasts (1000) must stack above.
  - Hosts own `maximized` and must pass useCallback-stable onRestore/onPopout.
  - Maximized wrapper owns its own scroll (overflow:auto, minHeight:0): fixed
    positioning escapes any scrollable ancestor the inline layout relied on, so
    the overlay must be a scroll container in its own right.
</constraints>
*/
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { theme } from '../theme';
import { CollapseIcon, PopoutIcon, CrossIcon, HeaderIconButton } from './shared';

interface TicketDetailStageProps {
  maximized: boolean;
  onRestore: () => void;
  onPopout: () => void;
  title: string;
  children: React.ReactNode;
}

const OVERLAY_INSET = theme.spacing.lg * 2;

const TicketDetailStage: React.FC<TicketDetailStageProps> = ({ maximized, onRestore, onPopout, title, children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  // Continuously captured while inline, so the placeholder knows the height to hold the
  // instant the wrapper leaves the flow (measuring after the flip would read overlay size).
  const lastInlineHeight = useRef(0);
  useLayoutEffect(() => {
    if (!maximized && wrapperRef.current) lastInlineHeight.current = wrapperRef.current.offsetHeight;
  });

  useEffect(() => {
    if (!maximized) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    wrapperRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onRestore(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      restoreFocusRef.current?.focus();
    };
  }, [maximized, onRestore]);

  return (
    <>
      {maximized && <div aria-hidden="true" style={{ height: lastInlineHeight.current, flexShrink: 0 }} />}
      {maximized && createPortal(
        <div
          data-testid="stage-backdrop"
          onClick={onRestore}
          style={{
            position: 'fixed', inset: 0, zIndex: 899,
            backgroundColor: 'rgba(10, 18, 83, 0.45)',
            animation: 'stx-fadeIn 0.2s ease',
          }}
        />,
        document.body,
      )}
      <div
        ref={wrapperRef}
        tabIndex={-1}
        {...(maximized ? { role: 'dialog', 'aria-modal': true, 'aria-label': title } : {})}
        style={maximized ? {
          position: 'fixed', inset: OVERLAY_INSET, zIndex: 900,
          backgroundColor: theme.colors.white,
          borderRadius: theme.borderRadius.md,
          boxShadow: theme.shadows.cardHover,
          padding: theme.spacing.lg,
          display: 'flex', flexDirection: 'column',
          overflow: 'auto', minHeight: 0,
          outline: 'none',
          animation: 'stx-fadeIn 0.2s ease',
        } : {
          display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, outline: 'none',
        }}
      >
        {maximized && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
            paddingBottom: theme.spacing.sm, marginBottom: theme.spacing.sm,
            borderBottom: `1px solid ${theme.colors.borderLight}`, flexShrink: 0,
          }}>
            <span style={{
              flex: 1, fontSize: 14, fontWeight: 600, color: theme.colors.navy,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {title}
            </span>
            <HeaderIconButton label="Wiederherstellen" onClick={onRestore}><CollapseIcon size={15} /></HeaderIconButton>
            <HeaderIconButton label="In eigenem Fenster öffnen" onClick={onPopout}><PopoutIcon size={15} /></HeaderIconButton>
            <HeaderIconButton label="Schließen" onClick={onRestore}><CrossIcon size={16} /></HeaderIconButton>
          </div>
        )}
        {children}
      </div>
    </>
  );
};

export default TicketDetailStage;
